/*
 OK FBI — Investigator Magnifying Glass
 Real-time high-fidelity circular magnifier for investigation sessions.

 Features:
   • Practical, sleek circular lens (180px diameter) with gold bezel & glass reflection.
   • Direct DOM synchronization: 100% crisp vector rendering for Arabic & English text.
   • Smooth 60+ FPS mouse-following via GPU translate3d; pin/unpin support.
   • Pointer-events transparent: full interaction with underlying buttons, tabs & controls.
   • Zero layout impact, non-destructive, clean unmount on deactivation.
   • Wheel-to-zoom (1.3× to 2.5×) + ESC to close.
   • OBS Broadcast Overlay sync (/overlay).
*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Search, Pin, PinOff } from "lucide-react";
import { api, BASE_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LENS_SIZE = 180;
const LENS_RADIUS = LENS_SIZE / 2;
const MIN_ZOOM = 1.3;
const MAX_ZOOM = 2.5;
const DEFAULT_ZOOM = 1.75;

/**
 * Creates a sanitized, scroll-synchronized clone of a DOM element.
 */
function createClonedView(sourceEl) {
  if (!sourceEl) return null;
  const clone = sourceEl.cloneNode(true);

  // Synchronize scroll positions of inner scrollable containers
  try {
    const origElements = sourceEl.querySelectorAll("*");
    const cloneElements = clone.querySelectorAll("*");
    const len = Math.min(origElements.length, cloneElements.length);
    for (let i = 0; i < len; i++) {
      const orig = origElements[i];
      if (orig && (orig.scrollTop > 0 || orig.scrollLeft > 0)) {
        const cln = cloneElements[i];
        if (cln) {
          cln.scrollTop = orig.scrollTop;
          cln.scrollLeft = orig.scrollLeft;
        }
      }
    }
  } catch (_) {}

  // Strip ignored elements and mute media
  try {
    const ignored = clone.querySelectorAll('[data-lens-ignore="1"], audio, video');
    ignored.forEach((el) => {
      if (el.tagName === "AUDIO" || el.tagName === "VIDEO") {
        try {
          el.pause();
          el.muted = true;
          el.src = "";
        } catch (_) {}
      } else {
        el.remove();
      }
    });
  } catch (_) {}

  return clone;
}

// =====================================================================
//   HOST MODE — interactive live lens with real DOM cloning
// =====================================================================
function LensHost() {
  const { user } = useAuth() || {};
  const authedRef = useRef(!!user);
  authedRef.current = !!user;

  const [active, setActive] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const lensRef = useRef(null);
  const innerRef = useRef(null);
  const cloneContainerRef = useRef(null);
  const mouseRef = useRef({
    x: typeof window !== "undefined" ? window.innerWidth / 2 : 400,
    y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
  });
  const pinnedPosRef = useRef(null);
  const rafRef = useRef(null);
  const zoomRef = useRef(DEFAULT_ZOOM);
  const lastStateSendRef = useRef(0);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Global mouse tracking so we always have the latest coordinates
  useEffect(() => {
    const trackMouse = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", trackMouse, { passive: true });
    return () => window.removeEventListener("mousemove", trackMouse);
  }, []);

  // Broadcast state to backend (for /overlay OBS mode)
  const broadcastState = useCallback(
    (forced = false) => {
      if (!authedRef.current) return;
      const now = performance.now();
      if (!forced && now - lastStateSendRef.current < 50) return;
      lastStateSendRef.current = now;
      const pos = pinnedPosRef.current || mouseRef.current;
      api
        .post("/lens/state", {
          active,
          pinned,
          x: pos.x,
          y: pos.y,
          zoom: zoomRef.current,
          vw: window.innerWidth,
          vh: window.innerHeight,
        })
        .catch(() => {});
    },
    [active, pinned]
  );

  useEffect(() => {
    broadcastState(true);
  }, [active, pinned, zoom, broadcastState]);

  // Synchronize DOM clone into the lens viewport
  const syncClone = useCallback(() => {
    if (!cloneContainerRef.current) return;
    const rootEl = document.getElementById("root");
    if (!rootEl) return;

    const clone = createClonedView(rootEl);
    if (!clone) return;

    clone.style.pointerEvents = "none";
    clone.style.userSelect = "none";

    // Set wrapper width and height to match the document scroll dimensions
    const scrollW = document.documentElement.scrollWidth || window.innerWidth;
    const scrollH = document.documentElement.scrollHeight || window.innerHeight;

    if (innerRef.current) {
      innerRef.current.style.width = `${scrollW}px`;
      innerRef.current.style.height = `${scrollH}px`;
    }

    cloneContainerRef.current.innerHTML = "";
    cloneContainerRef.current.appendChild(clone);
  }, []);

  // Update lens position & magnified coordinate alignment (GPU accelerated)
  const updateLens = useCallback(() => {
    const lens = lensRef.current;
    const inner = innerRef.current;
    if (!lens || !inner) return;

    const pos = pinnedPosRef.current || mouseRef.current;
    const z = zoomRef.current;
    const scrollX = window.scrollX || 0;
    const scrollY = window.scrollY || 0;

    // Position lens circle centered over cursor/pinned coordinate
    lens.style.transform = `translate3d(${pos.x - LENS_RADIUS}px, ${pos.y - LENS_RADIUS}px, 0)`;

    // Position & scale cloned DOM so that the point under the cursor is perfectly at the lens center
    const targetDocX = pos.x + scrollX;
    const targetDocY = pos.y + scrollY;
    const translateX = -targetDocX * z + LENS_RADIUS;
    const translateY = -targetDocY * z + LENS_RADIUS;

    inner.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${z})`;
  }, []);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateLens();
      broadcastState(false);
    });
  }, [updateLens, broadcastState]);

  // Initial clone & dynamic synchronization while active
  useEffect(() => {
    if (!active) return;

    syncClone();
    scheduleUpdate();

    // Re-clone on DOM changes (e.g. tabs change, items reveal)
    const rootEl = document.getElementById("root");
    let mutationTimer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => {
        syncClone();
        scheduleUpdate();
      }, 200);
    });

    if (rootEl) {
      observer.observe(rootEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "src"],
      });
    }

    // Update on scroll & resize
    let scrollTimer = null;
    const onScroll = () => {
      scheduleUpdate();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => syncClone(), 150);
    };

    const onResize = () => {
      syncClone();
      scheduleUpdate();
    };

    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      observer.disconnect();
      clearTimeout(mutationTimer);
      clearTimeout(scrollTimer);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [active, syncClone, scheduleUpdate]);

  // Mouse move listener while active
  useEffect(() => {
    if (!active) return;
    const onMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      if (!pinned) scheduleUpdate();
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [active, pinned, scheduleUpdate]);

  // Re-position when zoom / pin state changes
  useEffect(() => {
    if (active) scheduleUpdate();
  }, [active, zoom, pinned, scheduleUpdate]);

  // Wheel zoom (only active when lens is turned on)
  useEffect(() => {
    if (!active) return;
    const onWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +(z + delta).toFixed(2))));
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [active]);

  // ESC key closes the lens
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setActive(false);
        setPinned(false);
        pinnedPosRef.current = null;
        setZoom(DEFAULT_ZOOM);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  // Toggle active state
  const toggleActive = useCallback(() => {
    setActive((prev) => {
      const next = !prev;
      if (!next) {
        setPinned(false);
        pinnedPosRef.current = null;
        setZoom(DEFAULT_ZOOM);
      }
      return next;
    });
  }, []);

  // Toggle pin state
  const togglePin = useCallback(() => {
    if (!active) return;
    setPinned((prev) => {
      const next = !prev;
      pinnedPosRef.current = next ? { ...mouseRef.current } : null;
      return next;
    });
  }, [active]);

  return createPortal(
    <>
      {/* Lens Controls Toolbar */}
      <div
        dir="rtl"
        data-lens-ignore="1"
        data-testid="lens-toolbar"
        style={{
          position: "fixed",
          top: 12,
          right: 260,
          zIndex: 999999,
          display: "flex",
          gap: 6,
          pointerEvents: "auto",
        }}
      >
        <button
          onClick={toggleActive}
          data-testid="lens-toggle-btn"
          title="عدسة المحقق"
          aria-label="عدسة المحقق"
          style={{
            width: 34,
            height: 34,
            borderRadius: 4,
            background: active ? "rgba(197,160,89,0.22)" : "rgba(0,0,0,0.78)",
            border: `1px solid ${active ? "#c5a059" : "rgba(197,160,89,0.35)"}`,
            color: active ? "#f2de9f" : "#c5a059",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 140ms ease",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            boxShadow: active
              ? "0 0 0 1px rgba(197,160,89,0.3), 0 4px 14px rgba(0,0,0,0.6)"
              : "0 2px 8px rgba(0,0,0,0.5)",
          }}
        >
          <Search size={16} strokeWidth={1.7} />
        </button>

        {active && (
          <button
            onClick={togglePin}
            data-testid="lens-pin-btn"
            title={pinned ? "إلغاء تثبيت العدسة" : "تثبيت العدسة"}
            aria-label={pinned ? "إلغاء تثبيت العدسة" : "تثبيت العدسة"}
            style={{
              width: 34,
              height: 34,
              borderRadius: 4,
              background: pinned ? "rgba(197,160,89,0.25)" : "rgba(0,0,0,0.78)",
              border: `1px solid ${pinned ? "#c5a059" : "rgba(197,160,89,0.35)"}`,
              color: pinned ? "#f2de9f" : "#c5a059",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              boxShadow: pinned
                ? "0 0 0 1px rgba(197,160,89,0.3), 0 4px 14px rgba(0,0,0,0.6)"
                : "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            {pinned ? <PinOff size={14} strokeWidth={1.7} /> : <Pin size={14} strokeWidth={1.7} />}
          </button>
        )}
      </div>

      {/* Magnifying Glass Body (Active Only) */}
      {active && (
        <div
          ref={lensRef}
          data-lens-ignore="1"
          data-testid="investigator-lens"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: LENS_SIZE,
            height: LENS_SIZE,
            borderRadius: "50%",
            overflow: "hidden",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 999998,
            border: "2.5px solid #c5a059",
            boxShadow:
              "0 12px 36px rgba(0,0,0,0.9), " +
              "0 0 0 1px rgba(0,0,0,0.8), " +
              "inset 0 0 18px rgba(0,0,0,0.6), " +
              "inset 0 0 2px rgba(197,160,89,0.35)",
            willChange: "transform",
            transform: `translate3d(${mouseRef.current.x - LENS_RADIUS}px, ${
              mouseRef.current.y - LENS_RADIUS
            }px, 0)`,
          }}
        >
          {/* Scaled mirror container */}
          <div
            ref={innerRef}
            data-lens-inner="1"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
              userSelect: "none",
              transformOrigin: "0 0",
              willChange: "transform",
            }}
          >
            <div ref={cloneContainerRef} style={{ width: "100%", height: "100%" }} />
          </div>

          {/* Glass reflection highlight */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 35% 25%, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.01) 45%, rgba(0,0,0,0.25) 100%)",
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 0 22px rgba(0,0,0,0.4)",
              pointerEvents: "none",
            }}
          />

          {/* Delicate Center Reticle / Crosshair */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 10,
              height: 10,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 4,
                top: 0,
                width: 2,
                height: 10,
                background: "rgba(197,160,89,0.65)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 4,
                left: 0,
                width: 10,
                height: 2,
                background: "rgba(197,160,89,0.65)",
              }}
            />
          </div>

          {/* Zoom & Pin Badge */}
          <div
            data-testid="lens-zoom-value"
            style={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 9,
              fontFamily: "ui-monospace, monospace",
              color: "#f2de9f",
              background: "rgba(0,0,0,0.85)",
              padding: "2px 7px",
              borderRadius: 3,
              letterSpacing: "0.15em",
              border: "1px solid rgba(197,160,89,0.3)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.7)",
              pointerEvents: "none",
            }}
          >
            {zoom.toFixed(1)}×{pinned ? " · PIN" : ""}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

// =====================================================================
//   OVERLAY MODE — passive mirror for OBS Browser Source
// =====================================================================
function LensOverlay() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/lens/state");
        if (!stop) setState(data);
      } catch (_) {}
    };
    tick();
    const iv = setInterval(tick, 80);
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, []);

  if (!state?.active) return null;

  const scaleX = window.innerWidth / (state.vw || window.innerWidth);
  const scaleY = window.innerHeight / (state.vh || window.innerHeight);

  return createPortal(
    <div
      data-testid="investigator-lens-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: LENS_SIZE,
        height: LENS_SIZE,
        borderRadius: "50%",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 999998,
        border: "2.5px solid #c5a059",
        boxShadow: "0 12px 36px rgba(0,0,0,0.9), inset 0 0 18px rgba(0,0,0,0.6)",
        transform: `translate3d(${state.x * scaleX - LENS_RADIUS}px, ${
          state.y * scaleY - LENS_RADIUS
        }px, 0)`,
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 9,
          fontFamily: "ui-monospace, monospace",
          color: "#f2de9f",
          background: "rgba(0,0,0,0.85)",
          padding: "2px 7px",
          borderRadius: 3,
          letterSpacing: "0.15em",
          border: "1px solid rgba(197,160,89,0.3)",
        }}
      >
        {Number(state.zoom || DEFAULT_ZOOM).toFixed(1)}×{state.pinned ? " · PIN" : ""}
      </div>
    </div>,
    document.body
  );
}

// =====================================================================
//   ROOT
// =====================================================================
export function InvestigatorLens() {
  const location = useLocation();
  const isOverlay = location?.pathname === "/overlay";
  return isOverlay ? <LensOverlay /> : <LensHost />;
}

export default InvestigatorLens;
